import { useCallback } from "react";
import dayjs from "dayjs";
import { normalizeTo13Digits } from "../utils/helpers";
import { defaultDateConfig } from "../utils/constants";

export const useFormHandlers = ({
  setValue,
  eventStartDate,
  eventStartTime,
  matchDate,
  type,
  matchList,
  checkIn,
  ticketsList,
  notificationInUtc,
  setMatchDate,
  setSelectedMatchSeason,
  setSetDefaults,
  handleDisplayNotificationDates,
  setAdditionalLocations,
  clubId,
  setAwayTeam,
  setMatchName,
}) => {
  const handleTimeChange = useCallback(
    (timeValue) => {
      if (!timeValue) {
        // Time is optional. Clearing it must also drop the time component
        // from the stored timestamp, otherwise the old time silently persists.
        setValue("eventStartTime", null);
        if (eventStartDate) {
          setValue(
            "eventStartDate",
            dayjs.unix(eventStartDate).startOf("day").unix(),
          );
        }
        return;
      }

      const currentDate = eventStartDate
        ? dayjs.unix(eventStartDate)
        : matchDate
          ? dayjs.unix(matchDate)
          : dayjs();

      const newDateTime = currentDate
        .hour(timeValue.hour())
        .minute(timeValue.minute())
        .second(0);

      setValue("eventStartTime", timeValue);
      setValue("eventStartDate", newDateTime.unix());
    },
    [eventStartDate, matchDate, setValue],
  );

  const handleDateChange = useCallback(
    (value) => {
      if (!value) {
        setValue("eventStartDate", null);
        setValue("eventEndDate", null);
        setValue("showOnHomePageStart", null);
        setValue("showOnHomePageEnd", null);
        setValue("notificationInUtc", [null]);
        return;
      }

      const hours = eventStartTime?.hour() ?? 0;
      const minutes = eventStartTime?.minute() ?? 0;
      const seconds = eventStartTime?.second() ?? 0;

      const date = dayjs(value)
        .set("hour", hours)
        .set("minute", minutes)
        .set("second", seconds)
        .unix();

      setValue("eventStartDate", date);

      const configuration = defaultDateConfig[type];
      if (configuration) {
        const data = handleDisplayNotificationDates(date, configuration);
        if (data) {
          setValue("showOnHomePage", data.showOnHomePage);
          setValue("notificationInUtc", data.notificationInUtc);
        }
      }
    },
    [eventStartTime, type, setValue, handleDisplayNotificationDates],
  );

  /**
   * Handles match change - updates ALL match-related fields
   * including matchName, awayTeam, and isHome which are needed for EventsList display
   * Works for both create/copy and edit modes
   */
  const handleMatchChange = useCallback(
    (e) => {
      const value = e.target.value;

      if (!value) {
        // Clear all match-related fields
        setValue("matchId", "");
        setValue("matchName", "");
        setValue("isHome", undefined);
        setValue("awayTeam", undefined);
        setValue("eventStartDate", "");
        setValue("eventStartTime", null);
        setSetDefaults(true);
        setMatchDate(null);
        setSelectedMatchSeason(null);
        setValue("checkIn", { ...checkIn, season: null });
        // Clear component state
        if (setAwayTeam) setAwayTeam(null);
        if (setMatchName) setMatchName(null);
        return;
      }

      const selectedMatch = matchList.find(
        (match) => Number(match?.fixture_id) === Number(value),
      );

      if (selectedMatch && clubId) {
        const rawTimestamp = selectedMatch?.event_timestamp;
        const eventDate = dayjs(selectedMatch?.event_date);
        // Fall back to event_date when timestamp is 0/missing (unconfirmed kickoff)
        const startDate =
          rawTimestamp && rawTimestamp > 0
            ? rawTimestamp
            : eventDate.isValid()
              ? eventDate.unix()
              : null;

        // Calculate isHome: true if club is home team
        const isHome =
          Number(selectedMatch.homeTeam.team_id) === Number(clubId);

        // Calculate awayTeam: opponent team (the one that's NOT the club)
        const awayTeam = isHome
          ? selectedMatch.awayTeam
          : selectedMatch.homeTeam;

        // Generate matchName in the same format as create mode
        const matchName = `${selectedMatch.homeTeam.team_name} vs ${selectedMatch.awayTeam.team_name} (${eventDate.format("Do MMM, YYYY")})`;

        // Update all form fields
        setValue("matchId", value);
        setValue("matchName", matchName);
        setValue("isHome", isHome);
        setValue("awayTeam", awayTeam);
        setValue("eventStartDate", startDate);
        setValue("eventStartTime", startDate ? dayjs(startDate * 1000) : null);
        setSetDefaults(true);

        // Update component state (for MatchSelector display)
        if (setAwayTeam) setAwayTeam(awayTeam);
        if (setMatchName) setMatchName(matchName);
        setSelectedMatchSeason(selectedMatch?.league?.season);
        setMatchDate(eventDate.unix());
        setValue("checkIn", {
          ...checkIn,
          season: selectedMatch?.league?.season ?? null,
        });
      }
    },
    [
      matchList,
      checkIn,
      setValue,
      setMatchDate,
      setSelectedMatchSeason,
      setSetDefaults,
      clubId,
      setAwayTeam,
      setMatchName,
    ],
  );

  const handleTicketChange = useCallback(
    (value) => {
      if (value) {
        const selectedTicket = ticketsList?.find(
          (ticket) => ticket?.id === value,
        );

        setValue("ticketName", selectedTicket?.name || "");
        setValue("ticketCode", selectedTicket?.code || "");
        setValue("ticketId", String(selectedTicket?.id) || "");
        setValue("ticketMatchId", String(selectedTicket?.matchId) || "");
        setValue("isLinkTicket", true);
      } else {
        setValue("ticketName", "");
        setValue("ticketCode", "");
        setValue("ticketId", "");
        setValue("ticketMatchId", "");
        setValue("isLinkTicket", false);
      }
    },
    [ticketsList, setValue],
  );

  const handleNotificationChange = useCallback(
    (value, index) => {
      let utcTimestamp = null;

      if (value) {
        const dateStr = dayjs(value).format("YYYY-MM-DD");
        utcTimestamp = dayjs.utc(dateStr).startOf("day").valueOf();
        utcTimestamp = normalizeTo13Digits(utcTimestamp);
      }

      const newNotifications = [...notificationInUtc];
      newNotifications[index] = utcTimestamp;
      setValue("notificationInUtc", newNotifications);
    },
    [notificationInUtc, setValue],
  );

  const handleLocationChange = useCallback(
    (locations) => {
      setAdditionalLocations(locations);
      setValue("additionalLocations", locations);
    },
    [setAdditionalLocations, setValue],
  );

  return {
    handleTimeChange,
    handleDateChange,
    handleMatchChange,
    handleTicketChange,
    handleNotificationChange,
    handleLocationChange,
  };
};
